import { SceneManager } from "./core/SceneManager.js";
import { CameraManager } from "./core/CameraManager.js";
import { EventManager } from "./core/EventManager.js";
import { DimensionManager } from "./core/DimensionManager.js";
import { StateManager } from "./core/StateManager.js";
import { CartManager } from "./cart/CartManager.js";
import { ThumbnailRenderer } from "./core/ThumbnailRenderer.js";
import { LocalizationManager } from "./localization/LocalizationManager.js";
import { EMBED_ALLOWED_ORIGINS, SHOW_DEV_TOOLS } from "./config.js";
import {
  initNativeShell,
  hideSplashAfterFirstFrame,
  resolveLaunchURL,
  adoptLaunchParams,
  onAppUrlOpen,
  hydrateStorage,
} from "./platform/index.js";
import { initAssetCache, installURLModifier, checkForAssetUpdates } from "./core/AssetSource.js";
import { runWhenIdle } from "./utils/idle.js";
import { playSplash, dismissSplash } from "./ui/splash.js";

// Core application class
class FurnitureDesigner {
  constructor() {
    // Core managers
    this.sceneManager = null;
    this.cameraManager = null;
    this.eventManager = null;
    this.dimensionManager = null;
    this.stateManager = null;

    // Feature managers
    this.furnitureManager = null;
    this.materialManager = null;
    this.uiManager = null;
    this.cartManager = null;
    this.pathTracingManager = null;
    this.thumbnailRenderer = null;
    this.localizationManager = null;
    this.usdzExportManager = null;
    this.lightRigConsole = null;

    // Bind animate function once for performance
    this.animateBound = this.animate.bind(this);

    // Application state
    this.isPaused = false;
    this._pausePending = false;
    this.animationFrameId = null;

    // Initialize application
    this.init();
  }

  // ===== INITIALIZATION =====
  async init() {
    // The black native splash only bridges to #splash, so lift it now and start the animation.
    hideSplashAfterFirstFrame().then(playSplash);

    try {
      // Parse the URL params once and reuse them everywhere (also read in initializeState).
      // Natively the document is capacitor://localhost with no query, so a link that
      // opened the app arrives via the App plugin instead; adopting it onto location
      // keeps the readers that parse location.search directly in agreement.
      // Durable state first: CameraManager reads threejs_camera_state inside its own
      // constructor, so this has to land before any manager exists. Batched with the
      // launch URL so boot pays one bridge round-trip, not two.
      // The native build ships no catalog — every json, model and texture streams from
      // ASSET_ORIGIN. The on-disk index must load before anything resolves a path, but it
      // depends on neither of the other two, so all three share the one boot round-trip.
      const [, launchURL] = await Promise.all([
        hydrateStorage(),
        resolveLaunchURL(),
        initAssetCache(),
      ]);
      adoptLaunchParams(launchURL);
      this.urlParams = launchURL.searchParams;
      // Embed/easy mode: read once at startup so every manager can see it.
      // Triggered by ?embed=1 — hides all UI, disables editing, forces a white room,
      // and leaves only camera rotate + zoom (used for read-only embeds of presets).
      this.isEmbedMode = this.urlParams.get('embed') === '1';
      if (this.isEmbedMode) document.body.classList.add('embed-mode');
      // Single-element embed (?embed=1&element=cascais-d). Read before CameraManager
      // so it can pick the closer element framing. Null outside embed mode.
      this.embedElement = this.isEmbedMode ? this.urlParams.get('element') : null;

      // Localization must resolve before the first heading paints and before country-filtered material loads; it needs no other manager, so run it concurrently with scene setup. Embed mode skips it (read-only).
      // The URL modifier is installed before sceneManager.init() issues its first
      // room/sky load, so every streamed asset resolves against the cache from the start.
      this.sceneManager = new SceneManager();
      installURLModifier(this.sceneManager.getLoadingManager());
      if (this.isEmbedMode) {
        await this.sceneManager.init();
      } else {
        this.localizationManager = new LocalizationManager(this);
        await Promise.all([
          this.sceneManager.init(),
          this.localizationManager.init(),
        ]);
      }

      // Embed presentation config (from environments.json `settings.embed`),
      // available now that environment data has loaded — CameraManager reads it.
      this.embedSettings = this.sceneManager.environmentManager.getEmbedSettings();

      this.cameraManager = new CameraManager(this.sceneManager.getRenderer(), this);
      // Add both cameras to scene (light is attached to perspective camera)
      this.sceneManager.getScene().add(this.cameraManager.getPerspectiveCamera());
      this.sceneManager.getScene().add(this.cameraManager.getOrthographicCamera());

      this.eventManager = new EventManager(this);
      this.eventManager.init();

      this.dimensionManager = new DimensionManager(this);

      // Initialize feature managers
      await this.initializeManagers();

      // Initialize StateManager and load saved state
      await this.initializeState();

      // Initialize dynamic lighting system (after scene is fully set up)
      this.sceneManager.initializeDynamicLighting();

      // Setup page visibility detection
      this.setupVisibilityDetection();

      // Start render loop
      this.animate();

      // Native shell: status bar, Android back button, background-save. No-op on web.
      // After animate() so the back button has a built UI to unwind.
      initNativeShell(this);

      dismissSplash();
      // Hide with a delay so the layout modal + pre-baked webp thumbnails reveal before the heavy path-tracer init, keeping first paint unblocked.
      await this.sceneManager.hideLoadingScreen();

      // Path tracing loads lazily on first toggle; deliberately not warmed on idle (most sessions never use it, ~1.8MB).

      // Links opened while the app is already running. The current design is saved first:
      // the arriving layout replaces it, and losing unsaved work to a tapped link would be
      // the worst possible surprise.
      onAppUrlOpen(async (url) => {
        const preset = url.searchParams.get("preset");
        const layout = url.searchParams.get("layout");
        if (!preset && !layout) return;

        this.stateManager?.saveSceneState();
        if (preset) await this.uiManager?.loadLayoutById(preset);
        else await this.stateManager?.loadFromURL(layout);
      });

      // Catalog updates: the app ships the whole catalog, so this is never on the critical
      // path — it runs on idle after first paint and only downloads files whose content
      // hash differs from what shipped. Assets only; the app's own code always comes from
      // the store build.
      runWhenIdle(() => {
        checkForAssetUpdates().then(({ updated, bytes }) => {
          if (updated) {
            console.info(`[assets] updated ${updated} files (${(bytes / 1e6).toFixed(1)} MB)`);
          }
        });
      }, { timeout: 10000, fallbackDelay: 5000 });

      // Embed-only postMessage bridge: lets the host page swap fabric / leg color /
      // leg height live, without reloading the iframe.
      if (this.isEmbedMode) this.setupEmbedMessaging();
    } catch (error) {
      console.error('Fatal error during initialization:', error);
      dismissSplash({ immediate: true });
      // Show error message to user
      const loadingScreen = document.getElementById('loading-screen');
      if (loadingScreen) {
        const wrapper = document.createElement('div');
        wrapper.style.cssText = 'color:#ff4444;padding:20px;text-align:center';
        // The native build streams its whole catalog, so a first run with no connection
        // fails here rather than anywhere subtler. Say that, instead of showing a stack.
        const offline = error?.name === 'AssetUnavailableError';
        const heading = document.createElement('h2');
        heading.textContent = offline
          ? 'No connection'
          : 'Failed to initialize application';
        const msgEl = document.createElement('p');
        msgEl.textContent = offline
          ? 'The catalog needs to be downloaded once before you can design offline.'
          : error.message;
        const retryEl = document.createElement('p');
        retryEl.textContent = offline
          ? 'Connect to the internet and try again.'
          : 'Please refresh the page to try again.';
        wrapper.append(heading, msgEl, retryEl);

        if (offline) {
          const retryBtn = document.createElement('button');
          retryBtn.textContent = 'Retry';
          retryBtn.style.cssText =
            'margin-top:16px;padding:10px 24px;font-size:16px;border-radius:8px;border:0;background:#fff;color:#1a1a1a';
          retryBtn.addEventListener('click', () => window.location.reload());
          wrapper.appendChild(retryBtn);
        }
        loadingScreen.textContent = '';
        loadingScreen.appendChild(wrapper);
      }
    }
  }

  async initializeManagers() {
    // Import and initialize managers
    const { FurnitureManager } = await import(
      "./furniture/FurnitureManager.js"
    );
    const { MaterialManager } = await import("./materials/MaterialManager.js");
    const { UIManager } = await import("./ui/UIManager.js");

    this.sceneManager.setApp(this);

    this.furnitureManager = new FurnitureManager(this);
    this.materialManager = new MaterialManager(
      this.sceneManager.getLoadingManager(),
      this.sceneManager.getRenderer(),
      this,
    );
    this.uiManager = new UIManager(this);

    // Embed fast path (single-element AND preset/layout embeds): load only catalog +
    // material data (model, texture, scene, shadows). Skip thumbnails, cart, localization,
    // grids and USDZ — all UI/commerce concerns that pull backend calls or offscreen
    // renders and are never shown in a read-only embed.
    if (this.isEmbedMode) {
      await Promise.all([
        this.furnitureManager.init(),
        this.materialManager.init(),
      ]);
      await this.uiManager.init(); // early-returns in embed
      return;
    }

    this.thumbnailRenderer = new ThumbnailRenderer(
      this.sceneManager.getRenderer(),
    );
    this.cartManager = new CartManager(this);

    // Warm the layout-modal thumbnails (fire-and-forget) so they're ready when the modal renders, racing in parallel with the heavier init below.
    this.uiManager.preloadLayoutThumbnails();

    // Localization already resolved in init(); wire it, then run the (country-free) catalog in parallel with country-filtered material data.
    this.uiManager.setLocalizationManager(this.localizationManager);
    const furnitureReady = this.furnitureManager.init();

    await this.materialManager.init();
    await furnitureReady;

    // Connect MaterialManager to ThumbnailRenderer for textured thumbnails
    this.thumbnailRenderer.setMaterialManager(this.materialManager);

    // Connect FurnitureManager to ThumbnailRenderer for layout thumbnails
    this.thumbnailRenderer.setFurnitureManager(this.furnitureManager);

    await this.uiManager.init();
    await this.cartManager.init();
    // USDZ export (exporter chunk + fflate) loads lazily on the first AR export click via ensureUSDZExportManager() — never during startup.
  }

  async initializeState() {
    // Initialize StateManager
    this.stateManager = new StateManager(this);

    // Connect StateManager to SceneManager
    this.sceneManager.setStateManager(this.stateManager);

    // This browser's environment + view toggles, restored before any layout loads. No-op in embed.
    this.stateManager.applyPreferences();

    // Check URL parameters first (takes precedence over localStorage)
    const urlParams = this.urlParams;
    const presetParam = urlParams.get('preset');
    // Single catalog element by urlCode — embed-only (resolved in init()).
    const elementParam = this.embedElement;
    const layoutParam = urlParams.get('layout');

    // Optional ?preset= / ?element= overrides, e.g. &fabric=HC-04&legColor=light&boardColor=dark&elementYaw=180&position=Closed
    const fabricCode = urlParams.get('fabric');
    const legColor = urlParams.get('legColor');
    const legHeight = urlParams.get('legHeight');
    const boardColor = urlParams.get('boardColor');
    // ?element= only: turn the element on the spot, for baking an alternate product shot.
    const elementYaw = urlParams.get('elementYaw');
    const position = urlParams.get('position');
    const overrides = (fabricCode || legColor || legHeight || boardColor || elementYaw || position)
      ? { fabricCode, legColor, legHeight, boardColor, elementYaw, position }
      : null;

    if (presetParam) {
      // Short share link: load a named preset by its layouts.json id (e.g. ?preset=cascais-ra).
      const loaded = await this.uiManager.loadLayoutById(presetParam, overrides);
      if (!loaded && !this.isEmbedMode) {
        // Unknown id — fall back to the selection modal rather than an empty room.
        await this.uiManager.showLayoutSelectionModal();
      }
    } else if (elementParam) {
      // Showcase a single catalog element by urlCode. Unknown id → empty white room.
      await this.uiManager.loadElementByUrlCode(elementParam, overrides);
    } else if (layoutParam) {
      await this.stateManager.loadFromURL(layoutParam);
    } else if (!this.isEmbedMode) {
      // Show layout selection modal (user can choose a preset or start from scratch)
      // The modal will check localStorage and offer to restore previous session.
      // Embeds skip the modal and just show the (white) room.
      await this.uiManager.showLayoutSelectionModal();
    }

    if (this.isEmbedMode) {
      // Force a clean product-shot presentation regardless of the preset's saved
      // settings, driven by environments.json `settings.embed`. (changeEnvironment
      // is synchronous — no await needed.)
      const embed = this.embedSettings;
      this.sceneManager.changeEnvironment(embed.environment);
      this.sceneManager.getRoomBuilder().setWhiteFloor(embed.whiteFloor !== false);
      this.dimensionManager.setEnabled(embed.showDimensions === true);
      // null = keep the preset's shadow setting; otherwise force the configured one.
      if (embed.shadowQuality) this.sceneManager.setShadowQuality(embed.shadowQuality);
      this.applyEmbedFraming(presetParam);
    }
  }

  // A preset's own `embedCamera` overrides the environment-level framing, and a mirrored pair
  // (diagon-l / diagon-r) shoots from the mirrored side — so re-run on every preset the embed loads.
  applyEmbedFraming(presetId) {
    const preset = this.uiManager?.findCombinationById(presetId);
    if (preset?.embedCamera) this.cameraManager.applyEmbedCamera(preset.embedCamera);
  }


  // ===== EMBED LIVE-UPDATE BRIDGE =====
  // Host posts { type: 'setOptions', preset?, fabric?, legColor?, legHeight?, boardColor?,
  // position?, target?, animate? } to change the look without reloading the iframe. Reuses the
  // validated applyPresetOverrides path; `position` switches the variant (q6 Seater ↔ Bed).
  setupEmbedMessaging() {
    // One at a time: a `preset` switch reloads the layout, and a message arriving mid-load would
    // interleave two loadLayout calls into the same scene.
    let queue = Promise.resolve();

    window.addEventListener('message', (event) => {
      if (!EMBED_ALLOWED_ORIGINS.includes(event.origin)) return;

      const data = event.data;
      if (!data || data.type !== 'setOptions') return;

      queue = queue
        .then(() => this._applyEmbedOptions(data))
        .then((result) => this._postEmbedResult(event.origin, { type: 'optionsApplied', ...result }))
        .catch((error) => console.warn('Embed setOptions failed:', error));
    });

    // Signal the host so it can replay selections made before the iframe loaded; `positions` lets
    // it build (or hide) its own picker without restating the catalog.
    this._postEmbedResult('*', {
      type: 'embedReady',
      ...(this.uiManager?.embedPositionState() ?? {}),
    });
  }

  // Ack on the origin the message came from, so a host picker syncs to what actually applied.
  _postEmbedResult(origin, payload) {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(payload, origin);
    }
  }

  /**
   * Apply one host `setOptions` message. `preset` swaps the whole layout — that's how the shop's
   * orientation picker flips a mirrored pair (diagon-l ↔ diagon-r) — carrying the finishes with it,
   * since the reloaded preset arrives wearing its own catalog defaults.
   * @returns {Promise<{position?: string|null, positions?: string[]}>} what the host's picker should show
   */
  async _applyEmbedOptions(data) {
    const overrides = {
      fabricCode: data.fabric ?? null,
      legColor: data.legColor ?? null,
      legHeight: data.legHeight ?? null,
      boardColor: data.boardColor ?? null,
    };

    // Resolved first (the host may send a renamed id), so reloading what's already in scene can't
    // tear it down and rebuild it for nothing.
    const presetId = this.uiManager?.findCombinationById(data.preset)?.id;
    if (presetId && presetId !== this.stateManager?.getLoadedPresetId()) {
      if (await this.uiManager.loadLayoutById(presetId, overrides)) {
        this.applyEmbedFraming(presetId);
      }
    } else {
      await this.uiManager?.applyPresetOverrides(overrides);
    }

    // After the preset branch: a reloaded layout arrives in its catalog default pose, so a message
    // carrying both has to re-pose what it just loaded (pass animate:false — nothing to fold from).
    if (data.position != null) {
      this.uiManager?.applyEmbedPosition(data.position, { target: data.target, animate: data.animate });
    }

    // Render loop may be paused on window blur — ensure the change shows.
    this.resume();

    return this.uiManager?.embedPositionState() ?? {};
  }

  // ===== PAGE VISIBILITY DETECTION =====
  setupVisibilityDetection() {
    // Handle page visibility changes (tab switching, minimizing)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pause();
      } else {
        this.resume();
      }
    });

    // Handle window focus changes (alt-tabbing, clicking away)
    window.addEventListener('blur', () => {
      this.pause();
    });

    window.addEventListener('focus', () => {
      this.resume();
    });
  }

  pause() {
    if (this.isPaused) return;

    // A variant tween only advances from this loop, so pausing mid-fold would freeze the piece
    // half folded with nothing to finish it — defer, and animate() retries once it settles.
    // An embed hits this constantly: the iframe blurs on every click on the host page.
    this._pausePending = this.furnitureManager?.hasAnimatingFurniture() ?? false;
    if (this._pausePending) return;

    this.isPaused = true;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  resume() {
    this._pausePending = false;
    if (this.isPaused) {
      this.isPaused = false;
      // Re-enter through rAF, not animate() directly: its `currentTime` defaults to 0, which
      // against a live lastTime is a negative delta big enough to fling a running tween's nodes
      // out of frame. rAF's timestamp shares performance.now()'s clock, so the first delta is ~0.
      this.lastTime = performance.now();
      this.animationFrameId = requestAnimationFrame(this.animateBound);
    }
  }

  // ===== RENDER LOOP =====
  animate(currentTime = 0) {
    // Skip if paused
    if (this.isPaused) {
      return;
    }

    this.animationFrameId = requestAnimationFrame(this.animateBound);

    const frameStartTime = performance.now();

    // Calculate delta time; floored at 0 since a negative one drives a tween's eased t past
    // its from-pose instead of interpolating.
    if (!this.lastTime) this.lastTime = currentTime;
    const deltaTime = Math.max((currentTime - this.lastTime) / 1000, 0);
    this.lastTime = currentTime;

    try {
      // Cache camera reference for this frame
      const camera = this.cameraManager?.getCamera();

      // Update camera controls
      if (this.cameraManager) {
        this.cameraManager.update();
      }

      // Update door animation; a moving door needs its shadow re-rendered
      // (shadows are no longer auto-updated every frame).
      const doorManager = this.sceneManager?.getDoorManager();
      if (doorManager && doorManager.update(deltaTime)) {
        this.sceneManager.requestShadowUpdate();
      }

      // Also places the attachment-point pluses, after any tween has moved its piece.
      this.furnitureManager?.update(deltaTime);

      // A pause deferred by a running tween takes effect on the frame it settles.
      if (this._pausePending) this.pause();

      // Update managers
      this.materialManager?.update();
      this.dimensionManager?.update();

      // Update and render scene
      if (this.sceneManager && camera) {
        this.sceneManager.update(deltaTime, camera);

        // Render with path tracer or standard renderer
        const tracedFrame = this.pathTracingManager?.render();
        if (!tracedFrame) this.sceneManager.render(camera);

        // Dev light-rig gizmos draw on their own canvas, over either renderer.
        this.lightRigConsole?.update(camera);
      }

      // Calculate frame time and update UI manager
      const frameTime = performance.now() - frameStartTime;
      this.uiManager?.update(frameTime);
    } catch (error) {
      console.error('Error in animation loop:', error);
    }
  }

  // ===== PUBLIC API =====
  getScene() {
    return this.sceneManager.getScene();
  }
  getRenderer() {
    return this.sceneManager.getRenderer();
  }
  getCameraManager() {
    return this.cameraManager;
  }
  getEventManager() {
    return this.eventManager;
  }
  getCollisionSystem() {
    return this.eventManager?.collisionSystem ?? null;
  }
  getFurnitureManager() {
    return this.furnitureManager;
  }
  getMaterialManager() {
    return this.materialManager;
  }
  getUIManager() {
    return this.uiManager;
  }
  getCartManager() {
    return this.cartManager;
  }
  getSceneManager() {
    return this.sceneManager;
  }
  getDimensionManager() {
    return this.dimensionManager;
  }
  getStateManager() {
    return this.stateManager;
  }
  /**
   * Signal that scene state has changed and should be persisted soon.
   * Delegates to StateManager.debouncedSave() so the debounce is centralised.
   */
  notifyStateChanged() {
    // A committed edit changed shadow-casting geometry — refresh the (otherwise
    // static) shadow maps / contact blob. Done before the embed early-return so
    // read-only embeds still update their shadows.
    this.sceneManager?.requestShadowUpdate();
    this.dimensionManager?.invalidate();
    this.furnitureManager?.invalidateAttachmentMarkers();
    // Direct rather than waiting for debouncedSave's 1s timer to reach the save
    // chokepoint — a committed edit should restart the trace promptly.
    this.pathTracingManager?.invalidate();
    // Embeds are read-only — never overwrite the host's saved session.
    if (this.isEmbedMode) return;
    this.stateManager?.debouncedSave();
  }
  getPathTracingManager() {
    return this.pathTracingManager;
  }

  /**
   * Lazily load + construct a manager whose module is a dynamic-import chunk kept
   * out of the main bundle. Concurrent callers share one import promise, so the
   * chunk is fetched and the manager constructed only once. `key` is the instance
   * field; `loader` returns the imported module.
   */
  _ensureLazyManager(key, loader, construct) {
    if (this[key]) return Promise.resolve(this[key]);
    const promiseKey = `_${key}Promise`;
    if (!this[promiseKey]) {
      this[promiseKey] = loader().then((mod) => {
        this[key] = construct(mod);
        return this[key];
      });
    }
    return this[promiseKey];
  }

  /**
   * PathTracingManager chunk (three-gpu-pathtracer + BVH workers), fetched when
   * path tracing is first warmed (on idle) or toggled by the user. Null in embed mode.
   */
  ensurePathTracingManager() {
    if (this.isEmbedMode) return Promise.resolve(null);
    return this._ensureLazyManager(
      "pathTracingManager",
      () => import("./core/PathTracingManager.js"),
      ({ PathTracingManager }) => new PathTracingManager(this),
    );
  }
  /** Dev-only path-tracing light rig console — its own chunk, never fetched in prod. */
  ensureLightRigConsole() {
    // import.meta.env.DEV is statically false in a production build, so Rollup drops
    // the dynamic import below and the chunk is never emitted at all.
    if (!import.meta.env.DEV || !SHOW_DEV_TOOLS || this.isEmbedMode) return Promise.resolve(null);
    return this._ensureLazyManager(
      "lightRigConsole",
      () => import("./ui/LightRigConsole.js"),
      ({ LightRigConsole }) => new LightRigConsole(this),
    );
  }
  getThumbnailRenderer() {
    return this.thumbnailRenderer;
  }
  getLocalizationManager() {
    return this.localizationManager;
  }
  ensureUSDZExportManager() {
    return this._ensureLazyManager(
      "usdzExportManager",
      () => import("./export/USDZExportManager.js"),
      ({ USDZExportManager }) => new USDZExportManager(this),
    );
  }
}

// Initialize application when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  // Disable right-click context menu
  document.addEventListener("contextmenu", (e) => e.preventDefault());

  window.furnitureDesigner = new FurnitureDesigner();
});
